/**
 * Vite/React Router version of ShopFooter — kept in sync with
 * src/components/ShopFooter.tsx. Only difference: react-router import
 * (same in both, but this file is the authoritative entry for the Vite SPA).
 *
 * Purpose: Multi-column footer (brand block + 3 symmetric link columns + bottom bar).
 * Inputs: none (static, no props).
 * Outputs: <footer> element with nav landmarks, address, and copyright bar.
 * Constraints:
 *   - Three columns of exactly 4 items each (symmetry requirement).
 *   - All labels 1–2 words maximum — no long CCPA verbatim text.
 *   - data-testid="footer-cookie-preferences-link" preserved on Cookies/consent link.
 *   - Single horizontal divider (between top region and bottom bar).
 *   - Address block is part of the brand column, not a separate section.
 *   - Dark RWB: navy-deep bg, gold accent (#b22234 via gold token), white text.
 * SPORT: src/components/ShopFooter.tsx · src/components/vite/ShopFooter.tsx (keep in sync)
 */

import { Link } from 'react-router'

// ─── Column data ──────────────────────────────────────────────────────────────

const SHOP_LINKS = [
  { label: 'All Products', to: '/products' },
  { label: 'Build Your Own', to: '/design' },
  { label: 'Catalog', to: '/catalog' },
  { label: 'Track Order', to: '/orders' },
]

const SUPPORT_LINKS: { label: string; to?: string; href?: string }[] = [
  { label: 'Shipping', to: '/shipping' },
  { label: 'Returns', to: '/returns' },
  { label: 'Refunds', to: '/refund' },
  { label: 'Contact', to: '/contact' },
]

/** Legal — 4 items; Cookies link carries the cookie-preferences testId for tests. */
const LEGAL_LINKS: { label: string; to?: string; href?: string; testId?: string }[] = [
  { label: 'Privacy', to: '/legal/privacy' },
  { label: 'Terms', to: '/legal/terms' },
  {
    label: 'Cookies',
    to: '/legal/cookies',
    testId: 'footer-cookie-preferences-link',
  },
  { label: 'Do Not Sell', href: 'https://veterans.pro/legal/do-not-sell' },
]

// ─── FooterLink helper ────────────────────────────────────────────────────────

interface LinkItemProps {
  label: string
  to?: string
  href?: string
  testId?: string
}

function FooterLink({ label, to, href, testId }: LinkItemProps) {
  const cls =
    'text-white/55 hover:text-gold transition-colors duration-150 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gold rounded'
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {label}
      </Link>
    )
  }
  return (
    <a href={href} className={cls} data-testid={testId}>
      {label}
    </a>
  )
}

// ─── ShopFooter ──────────────────────────────────────────────────────────────

export function ShopFooter() {
  return (
    <footer
      aria-label="Site footer"
      className="border-t border-white/10 bg-navy-deep mt-24"
    >
      {/* ── Top region: brand block + 3 link columns ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {/* Brand block */}
          <div className="lg:col-span-1 flex flex-col">
            {/* Wordmark */}
            <p className="text-xl font-extrabold tracking-tight mb-3">
              <span className="text-white">Shop</span>
              <span className="text-gold-text">.Veterans</span>
            </p>

            {/* Brand blurb */}
            <p className="text-sm text-white/55 leading-relaxed max-w-xs mb-4">
              Custom embroidered and printed gear honoring every branch of the U.S. Armed Forces.
              Veteran-owned and operated — every order is made-to-order in the USA.
              A portion of every sale supports veteran-focused organizations and community programs.
            </p>

            {/* Address */}
            <address className="not-italic text-xs text-white/60 leading-relaxed mb-5">
              Veterans.Pro
              <br />
              1679 S. Dupont Hwy, Suite 100
              <br />
              Dover, DE 19901
              <br />
              <a
                href="mailto:shop@veterans.pro"
                className="hover:text-gold-text transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gold rounded"
              >
                shop@veterans.pro
              </a>
            </address>

            {/* Back link */}
            <a
              href="https://veterans.pro"
              className="inline-block text-xs font-semibold text-white/60 hover:text-gold-text transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gold rounded tracking-wide uppercase"
            >
              ← Veterans.Pro
            </a>
          </div>

          {/* Shop column */}
          <nav aria-label="Shop links">
            <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-4">Shop</p>
            <ul className="space-y-2.5 list-none m-0 p-0">
              {SHOP_LINKS.map((l) => (
                <li key={l.to}>
                  <FooterLink label={l.label} to={l.to} />
                </li>
              ))}
            </ul>
          </nav>

          {/* Support column */}
          <nav aria-label="Support links">
            <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-4">Support</p>
            <ul className="space-y-2.5 list-none m-0 p-0">
              {SUPPORT_LINKS.map((l) => (
                <li key={l.label}>
                  <FooterLink label={l.label} to={l.to} href={l.href} />
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal column */}
          <nav aria-label="Legal links">
            <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-4">Legal</p>
            <ul className="space-y-2.5 list-none m-0 p-0">
              {LEGAL_LINKS.map((l) => (
                <li key={l.label}>
                  <FooterLink label={l.label} to={l.to} href={l.href} testId={l.testId} />
                </li>
              ))}
            </ul>
          </nav>

        </div>
      </div>

      {/* ── Single divider ── */}
      <div className="border-t border-white/8" />

      {/* ── Bottom bar ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/60">
        <p>© {new Date().getFullYear()} Veterans.Pro. Designed for those who served.</p>
        <p className="flex items-center gap-1.5">
          <span>🇺🇸</span>
          <span>Made in the USA</span>
        </p>
      </div>
    </footer>
  )
}
